// Adapted from React Bits Particles by David Haz.
// https://reactbits.dev/backgrounds/particles — see LICENSE.md.
import { useEffect, useRef } from 'react';
import { Camera, Geometry, Mesh, Program, Renderer } from 'ogl';
import { useTheme } from '@/contexts/useTheme';

const vertex = /* glsl */ `
  attribute vec3 position;
  attribute vec4 random;
  attribute vec3 color;
  
  uniform mat4 modelMatrix;
  uniform mat4 viewMatrix;
  uniform mat4 projectionMatrix;
  uniform float uTime;
  uniform float uSpread;
  uniform float uBaseSize;
  uniform float uSizeRandomness;
  
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vRandom = random;
    vColor = color;
    
    vec3 pos = position * uSpread;
    pos.z *= 10.0;
    
    vec4 mPos = modelMatrix * vec4(pos, 1.0);
    float t = uTime;
    mPos.x += sin(t * random.z + 6.28 * random.w) * mix(0.1, 1.5, random.x);
    mPos.y += sin(t * random.y + 6.28 * random.x) * mix(0.1, 1.5, random.w);
    mPos.z += sin(t * random.w + 6.28 * random.y) * mix(0.1, 1.5, random.z);
    
    vec4 mvPos = viewMatrix * mPos;
    if (uSizeRandomness == 0.0) {
      gl_PointSize = uBaseSize;
    } else {
      gl_PointSize = clamp((uBaseSize * (1.0 + uSizeRandomness * (random.x - 0.5))) / length(mvPos.xyz), 2.5, 12.0);
    }
    
    gl_Position = projectionMatrix * mvPos;
  }
`;

const fragment = /* glsl */ `
  precision highp float;
  
  uniform float uTime;
  uniform float uAlphaParticles;
  varying vec4 vRandom;
  varying vec3 vColor;
  
  void main() {
    vec2 uv = gl_PointCoord.xy;
    float d = length(uv - vec2(0.5));
    
    if(uAlphaParticles < 0.5) {
      if(d > 0.5) {
        discard;
      }
      gl_FragColor = vec4(vColor, 1.0);
    } else {
      float circle = smoothstep(0.5, 0.4, d) * 0.8;
      gl_FragColor = vec4(vColor, circle);
    }
  }
`;

export function Particles() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
    let renderer: Renderer;
    try {
      renderer = new Renderer({ dpr: pixelRatio, depth: false, alpha: true });
    } catch {
      // The page remains fully usable when WebGL is unavailable.
      return;
    }
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    container.appendChild(gl.canvas);

    const camera = new Camera(gl, { fov: 15 });
    camera.position.set(0, 0, 20);

    const count = container.clientWidth < 640 ? 190 : 420;
    const positions = new Float32Array(count * 3);
    const randoms = new Float32Array(count * 4);
    const colors = new Float32Array(count * 3);
    const color = (
      getComputedStyle(container).color.match(/[\d.]+/g) ?? ['0', '0', '0']
    )
      .slice(0, 3)
      .map(channel => Number(channel) / 255);

    for (let i = 0; i < count; i++) {
      let x: number, y: number, z: number, length: number;
      do {
        x = Math.random() * 2 - 1;
        y = Math.random() * 2 - 1;
        z = Math.random() * 2 - 1;
        length = x * x + y * y + z * z;
      } while (length > 1 || length === 0);
      const radius = Math.cbrt(Math.random());
      positions.set([x * radius, y * radius, z * radius], i * 3);
      randoms.set(
        [Math.random(), Math.random(), Math.random(), Math.random()],
        i * 4
      );
      colors.set(color, i * 3);
    }

    const geometry = new Geometry(gl, {
      position: { size: 3, data: positions },
      random: { size: 4, data: randoms },
      color: { size: 3, data: colors },
    });
    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uTime: { value: 0 },
        uSpread: { value: 10 },
        uBaseSize: { value: 180 * pixelRatio },
        uSizeRandomness: { value: 1 },
        uAlphaParticles: { value: 1 },
      },
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const particles = new Mesh(gl, { mode: gl.POINTS, geometry, program });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let lastTime = performance.now();
    let elapsed = 0;

    const draw = () => renderer.render({ scene: particles, camera });
    const update = (now: number) => {
      frame = 0;
      if (document.hidden || reducedMotion.matches) return;
      const delta = Math.min(now - lastTime, 50);
      lastTime = now;
      elapsed += delta * 0.18;
      program.uniforms.uTime.value = elapsed * 0.001;
      particles.rotation.x = Math.sin(elapsed * 0.0002) * 0.1;
      particles.rotation.y = Math.cos(elapsed * 0.0005) * 0.15;
      particles.rotation.z += delta * 0.000022;
      draw();
      frame = requestAnimationFrame(update);
    };
    const syncAnimation = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      if (document.hidden) return;
      draw();
      if (!reducedMotion.matches) {
        lastTime = performance.now();
        frame = requestAnimationFrame(update);
      }
    };
    const resize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!width || !height) return;
      renderer.setSize(width, height);
      camera.perspective({ aspect: width / height });
      if (!document.hidden) draw();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    document.addEventListener('visibilitychange', syncAnimation);
    reducedMotion.addEventListener('change', syncAnimation);
    resize();
    syncAnimation();

    // ThemeProvider updates the root class in an effect; read its final colors
    // on the next frame so switching themes cannot leave the old palette behind.
    const colorFrame = requestAnimationFrame(() => {
      const themeColor = (
        getComputedStyle(container).color.match(/[\d.]+/g) ?? ['0', '0', '0']
      )
        .slice(0, 3)
        .map(channel => Number(channel) / 255);
      for (let i = 0; i < count; i++) colors.set(themeColor, i * 3);
      geometry.attributes.color.needsUpdate = true;
      if (!document.hidden) draw();
    });

    return () => {
      cancelAnimationFrame(colorFrame);
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', syncAnimation);
      reducedMotion.removeEventListener('change', syncAnimation);
      geometry.remove();
      program.remove();
      gl.canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [theme]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 -z-10 overflow-hidden text-primary opacity-75 dark:text-foreground [&>canvas]:block"
    />
  );
}
